import { EvidencePackButton } from "../components/EvidencePackButton";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api, money, riskColor, shortDate } from "../lib/api";
import { Panel, NeonButton } from "../components/common";
import { StatusBadge } from "../components/DisputeCard";
import { EvidenceTimeline } from "../components/EvidenceTimeline";
import { BankNegotiationPanel } from "../components/BankNegotiationPanel";
import { RepresentmentDrafter } from "../components/RepresentmentDrafter";
import { EvidencePackage } from "../components/EvidencePackage";
import { StrategyPanel } from "../components/StrategyPanel";
import { ExplainPanel } from "../components/ExplainPanel";
import { RepresentmentSimulator } from "../components/RepresentmentSimulator";
import { useAuth } from "../lib/auth";
import { EvidenceScoreBadge } from "../components/manual/EvidenceScoreBadge";
import { ResolutionActions } from "../components/whop/ResolutionActions";

const Fingerprint = ({ tx, consent }) => {
  const rows = [
    ["Transaction", tx.id], ["Product", tx.product_name], ["Card BIN", tx.card_bin],
    ["Card / IP country", `${tx.country} / ${tx.ip_country}`], ["IP", tx.ip_address], ["Device", tx.device_fingerprint],
    ["3DS / AVS / CVV", `${tx.is_3ds ? "Y" : "N"} / ${tx.avs_match ? "Y" : "N"} / ${tx.cvv_match ? "Y" : "N"}`],
    ["Consent", consent ? `signed ${shortDate(consent.created_at)}` : "none"],
  ];
  return (
    <dl className="grid grid-cols-2 gap-x-4 gap-y-3" data-testid="transaction-fingerprint">
      {rows.map(([k, v]) => (
        <div key={k}><dt className="label-xs">{k}</dt><dd className="font-mono text-xs mt-1 truncate">{v}</dd></div>
      ))}
    </dl>
  );
};

export default function DisputeDetail() {
  const { isOwner, canWrite } = useAuth();
  const { id } = useParams();
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/disputes/${id}`).then((r) => setData(r.data)), [id]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (status) => {
    await api.patch(`/disputes/${id}/status`, { status });
    toast.success(`Marked ${status}`);
    load();
  };

  if (!data) return <p className="font-mono text-sm text-white/35">decrypting case file…</p>;
  const owner = isOwner;
  const writer = canWrite;
  const { dispute: d, transaction: tx, evidence, consent, analysis, bank, representments } = data;
  return (
    <div data-testid="dispute-detail">
      <Link to="/app/disputes" className="inline-flex items-center gap-2 text-xs text-white/45 hover:text-white font-mono mb-6" data-testid="back-to-disputes"><ArrowLeft className="w-3 h-3" /> docket</Link>
      <div className="flex flex-wrap items-end justify-between gap-6 mb-8 rise">
        <div>
          <div className="flex items-center gap-3">
            <p className="font-mono text-xs text-white/35">{d.id}</p>
            <StatusBadge status={d.status} testid="detail-status" />
          </div>
          <h1 className="font-mono text-5xl font-extrabold tracking-tighter mt-3 tabular" data-testid="detail-amount">{money(d.amount)}</h1>
          <p className="text-[#AAA] mt-2">{analysis.reason_label} · {d.network?.toUpperCase()} {analysis.reason_code} · {d.issuer_bank} · {d.customer_name}</p>
          <p className="font-mono text-xs mt-2" style={{ color: riskColor(tx.risk_score) }}>pre-payment risk {tx.risk_score} · due {shortDate(d.due_by)}</p>
          <EvidenceScoreBadge key={evidence.length} disputeId={d.id} />
          {d.autopilot && <p className="mt-2 text-xs border-l-2 border-brand pl-3 text-white/70" data-testid="dispute-autopilot"><b className="text-brand font-mono">AUTOPILOT · {d.autopilot.decision} · {d.autopilot.confidence}%</b> {d.autopilot.detail}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          <EvidencePackButton disputeId={d.id} testid="dispute-evidence-pack-btn" />
          {writer && <RepresentmentSimulator disputeId={d.id} label="Simulate this representment" />}
          {owner && <>
          <NeonButton variant="ghost" onClick={() => setStatus("won")} data-testid="mark-won-btn">Mark won</NeonButton>
          <NeonButton variant="ghost" onClick={() => setStatus("lost")} data-testid="mark-lost-btn">Mark lost</NeonButton>
          <NeonButton variant="danger" onClick={() => setStatus("accepted")} data-testid="accept-dispute-btn">Accept</NeonButton>
          </>}
        </div>
      </div>
      <div className="grid grid-cols-12 gap-6">
        <div className="col-span-12 xl:col-span-7 space-y-6">
          <Panel title="AI representment drafter · claude sonnet 4.5" testid="panel-drafter">
            <ResolutionActions key={`r${representments.length}`} d={d} letter={representments[0]?.content} onDone={load} />
            <RepresentmentDrafter key={representments.length} disputeId={d.id} reason={d.reason} issuer={d.issuer_bank} dueBy={d.due_by} drafts={representments} onSaved={load} />
          </Panel>
          <Panel title="Transaction fingerprint"><Fingerprint tx={tx} consent={consent} /></Panel>
        </div>
        <div className="col-span-12 xl:col-span-5 space-y-6">
          <Panel title="Why this score? · what to improve" testid="panel-explain"><ExplainPanel key={d.status} disputeId={d.id} /></Panel>
          <Panel title="Predictive strategy · this dispute" testid="panel-strategy"><StrategyPanel key={d.status} disputeId={d.id} onChanged={load} /></Panel>
          <Panel title="Bank negotiation intelligence"><BankNegotiationPanel a={analysis} bank={bank} /></Panel>
          <Panel title="Evidence timeline"><EvidenceTimeline evidence={evidence} transactionId={tx.id} /></Panel>
          <Panel title="Sealed evidence packages · merkle + rfc 3161"><EvidencePackage disputeId={d.id} /></Panel>
        </div>
      </div>
    </div>
  );
}
